import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, User, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const reason = sessionStorage.getItem("khgb_logout_reason");
    if (reason) {
      sessionStorage.removeItem("khgb_logout_reason");
      // defer: Toaster (rendered after the router in App.js) subscribes after this effect runs
      setTimeout(() => toast.info(reason, { id: "logout-reason" }), 100);
    }
  }, []);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success("Giriş başarılı");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-1 bg-[#0F172A] text-white p-16 flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="h-12 w-12 rounded-lg bg-emerald-600 flex items-center justify-center font-bold text-xl">
              K
            </div>
            <div>
              <div className="font-display font-bold text-lg">KHGB</div>
              <div className="text-xs text-slate-400">Köylere Hizmet Götürme Birliği</div>
            </div>
          </div>
          <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
            Köylere hizmet,<br />
            <span className="text-emerald-400">tek bir panelden</span>.
          </h2>
          <p className="mt-6 text-slate-300 max-w-md text-sm leading-relaxed">
            Köy kartları, çalışma kayıtları, altyapı projeleri, yatırımlar, dilekçeler ve raporlar — hepsi tek bir kapalı yönetim sisteminde.
          </p>
        </div>
        <div className="relative z-10 text-xs text-slate-500">
          © {new Date().getFullYear()} Köylere Hizmet Götürme Birliği
        </div>
        {/* subtle grid */}
        <div className="absolute inset-0 opacity-[0.06]"
             style={{
               backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
               backgroundSize: "24px 24px",
             }} />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-md bg-emerald-600 flex items-center justify-center font-bold text-white">
              K
            </div>
            <div className="font-display font-bold">KHGB Yönetim</div>
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mb-2">Oturum Aç</h1>
          <p className="text-sm text-slate-500 mb-8">
            Sisteme girmek için kullanıcı adı ve şifrenizi giriniz.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-slate-700 text-sm font-medium">Kullanıcı Adı</Label>
              <div className="relative mt-1.5">
                <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Kullanıcı adınız"
                  className="pl-9 h-11"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 text-sm font-medium">Şifre</Label>
              <div className="relative mt-1.5">
                <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-9 h-11"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-medium"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {loading ? "Giriş Yapılıyor..." : "Giriş Yap"}
            </Button>
          </div>

          <p className="mt-10 text-xs text-slate-400">
            Bu sistem yalnızca yetkili personel içindir. Şifrenizi unuttuysanız sistem yöneticisine başvurun.
          </p>
        </form>
      </div>
    </div>
  );
}
